import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useDeliveryManagement } from "./useHook";
import OutStationZoneModal from "../../Components/Modals/OutStationZoneModal/OutStationZone";
import { MdDelete, MdEdit } from "react-icons/md";
import Loader from "../../Utils/loader";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import { FiEdit2, FiPlus } from "react-icons/fi";

function OutStationZonePage() {
  const {
    getOutStationZones,
    addOutStationZone,
    updateOutStationZone,
    deleteOutStationZone,
  } = useDeliveryManagement();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zones, setZones] = useState([]);
  const [editZone, setEditZone] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setEditZone(null);
  };

  const fetchZones = async () => {
    try {
      setIsLoading(true);
      const response = await getOutStationZones(setZones, offset, limit);

      if (response?.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleAddOrUpdateZone = async (zoneData, zoneId = null) => {
    try {
      if (zoneId) {
        const response = await updateOutStationZone(zoneId, zoneData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Zone Updated",
            text: "Your zone has been successfully updated!",
            confirmButtonText: "OK",
          });
        }
      } else {
        const response = await addOutStationZone(zoneData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Zone Added",
            text: "Your zone has been successfully added!",
            confirmButtonText: "OK",
          });
        }
      }
      await fetchZones();
    } catch (error) {
      // Extract the specific error message from the backend response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while saving the zone. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Failed to Save Zone",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    }
    closeModal();
  };

  const handleEditZone = (zone) => {
    setEditZone(zone);
    openModal();
  };

  const handleDeleteZone = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteOutStationZone(id);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "The zone has been deleted.",
            confirmButtonText: "OK",
          });
          await fetchZones();
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to Delete",
          text: "An error occurred while deleting the zone. Please try again.",
          confirmButtonText: "OK",
        });
      }
    }
  };

  if (isLoading) {
    return <Loader message="Loading zones..." />;
  }

  return (
    <div>
      <div>
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <h2 className="text-2xl font-bold">Manage Out Station Zones</h2>

          <button onClick={openModal} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add Out Station Zone
          </button>
        </div>

        <div className="bg-white w-full rounded-lg pagination-shadow my-5">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th">Sr. No</th>
                  <th className="table-th">Zone Name</th>
                  <th className="table-th">Multiplier</th>
                  <th className="table-th">Description</th>
                  <th className="table-th">Far Flung</th>
                  <th className="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                {zones.length > 0 ? (
                  zones.map((zone, index) => (
                    <tr
                      key={zone.id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                    >
                      <td className="table-td">{offset + index + 1}</td>
                      <td className="table-td">{zone.zone_name}</td>
                      <td className="table-td">{zone.multiplier}</td>
                      <td className="table-td">{zone.region_description}</td>
                      <td className="table-td">
                        {zone.isFarFlung ? "Yes" : "No"}
                      </td>

                      <td className="table-td flex space-x-2 mt-3">
                        <button
                          className="btn btn-edit text-white"
                          onClick={() => handleEditZone(zone)}
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                        <button
                          className="btn btn-delete text-white"
                          onClick={() => handleDeleteZone(zone.id)}
                        >
                          <MdDelete className="text-white" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-6 text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Component */}
          {pagination.totalPages > 1 && (
            <div className="mt-6">
              <GlobalPagination
                offset={offset}
                limit={limit}
                total={pagination.totalRecords}
                onPageChange={handlePageChange}
                className="mt-4"
              />
            </div>
          )}
        </div>
      </div>

      <OutStationZoneModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleAddOrUpdateZone}
        editZone={editZone}
      />
    </div>
  );
}

export default OutStationZonePage;
